/**
 * Teacher-admin vector-store and document CRUD (`/chat/admin/vector-stores…`,
 * `/chat/admin/documents…`) with mock-mode fallbacks. Application code
 * imports from `src/api.ts`.
 */
import type {
  AdminDocumentListResponse,
  AdminDocumentSummaryWire,
  AdminVectorStoreListResponse,
  AdminVectorStoreSummaryWire,
  CreateAdminVectorStoreRequest,
  UpdateAdminVectorStoreRequest,
} from '../types/backend';
import { DOMAIN_ERROR_MESSAGES_DA } from '../types/backend';
import { parseBackendUtcTimestamp } from '../lib/liveIntegration';
import { nowISO, uid } from '../lib/ids';
import { fileToUploadedMetadata } from '../lib/vectorStoreValidation';
import type {
  CreateVectorStoreInput,
  SaveVectorStoreEditsInput,
  UpdateVectorStoreInput,
  VectorStore,
  VectorStoreDocument,
} from '../types';
import {
  ApiError,
  CONTRACT_MESSAGE,
  USE_LIVE,
  fetchBlob,
  fetchJson,
  fetchMultipart,
  mapAdminError,
  resolveDashboardAuth,
  encodeApiSegment,
} from './http';
import {
  mockAdminChatbots,
  mockAdminDocuments,
  mockAdminVectorStores,
} from './adminMockData';

export const ADMIN_VECTOR_STORE_LIST_ERROR_MESSAGE = 'Kunne ikke hente vector stores.';
const ADMIN_VECTOR_STORE_CREATE_ERROR_MESSAGE = 'Kunne ikke oprette vector store.';
const ADMIN_VECTOR_STORE_GET_ERROR_MESSAGE = 'Kunne ikke hente vector store.';
const ADMIN_VECTOR_STORE_UPDATE_ERROR_MESSAGE = 'Kunne ikke gemme vector store.';
const ADMIN_VECTOR_STORE_DELETE_ERROR_MESSAGE = 'Kunne ikke slette vector store.';
const ADMIN_VECTOR_STORE_UPLOAD_DISPATCH_ERROR_MESSAGE =
  'Filen blev uploadet, men behandlingen kunne ikke startes.';

function wireToVectorStore(wire: AdminVectorStoreSummaryWire): VectorStore {
  try {
    parseBackendUtcTimestamp(wire.created_at);
    parseBackendUtcTimestamp(wire.updated_at);
  } catch {
    throw new ApiError({
      httpStatus: 502,
      code: null,
      userMessage: CONTRACT_MESSAGE,
    });
  }
  return {
    id: wire.id,
    navn: wire.name,
    beskrivelse: wire.description ?? '',
    tilladChunking: true,
    synligForLaerere: wire.visible_to_teachers ?? false,
    uploadedFile: null,
    embeddingModel: wire.embedding_model,
    chunkSize: wire.chunk_size,
    chunkOverlap: wire.chunk_overlap,
    defaultTopK: wire.default_top_k,
    createdAt: wire.created_at,
    updatedAt: wire.updated_at,
  };
}

function wireToVectorStoreDocument(wire: AdminDocumentSummaryWire): VectorStoreDocument {
  validateDocumentWireTimestamps(wire);
  return {
    id: wire.id,
    vectorStoreId: wire.vector_store_id,
    filename: wire.filename,
    status: wire.status,
    errorMessage: wire.error_message,
    createdAt: wire.created_at,
    updatedAt: wire.updated_at,
  };
}

function mockVectorStoreInUseError(): ApiError {
  return new ApiError({
    httpStatus: 409,
    code: 'vector_store_in_use',
    userMessage: DOMAIN_ERROR_MESSAGES_DA.vector_store_in_use,
  });
}

function mockStoreLinkedByChatbot(storeId: string): boolean {
  return mockAdminChatbots.some((chatbot) => chatbot.vectorStoreId === storeId);
}

function seedMockDocumentsForStore(storeId: string, files: File[]): void {
  const now = nowISO();
  const docs = files.map((file) => ({
    id: uid(),
    vectorStoreId: storeId,
    filename: file.name,
    status: 'ready' as const,
    errorMessage: null,
    createdAt: now,
    updatedAt: now,
  }));
  mockAdminDocuments.set(storeId, docs);
}

export async function listVectorStores(): Promise<VectorStore[]> {
  if (!USE_LIVE) {
    return [...mockAdminVectorStores];
  }
  try {
    const data = await fetchJson<AdminVectorStoreListResponse>('/chat/admin/vector-stores', {
      ...resolveDashboardAuth(),
    });
    return data.vector_stores.map(wireToVectorStore);
  } catch (error) {
    throw mapAdminError(error, ADMIN_VECTOR_STORE_LIST_ERROR_MESSAGE);
  }
}

function validateDocumentWireTimestamps(wire: AdminDocumentSummaryWire): void {
  try {
    parseBackendUtcTimestamp(wire.created_at);
    parseBackendUtcTimestamp(wire.updated_at);
  } catch {
    throw new ApiError({
      httpStatus: 502,
      code: null,
      userMessage: CONTRACT_MESSAGE,
    });
  }
}

function buildUploadFailureError(
  error: unknown,
  filename: string,
  uploadedBeforeFailure: number,
  total: number,
): ApiError {
  const normalized = mapAdminError(error, ADMIN_VECTOR_STORE_CREATE_ERROR_MESSAGE);
  if (normalized.code === 'dispatch_failed') {
    return new ApiError({
      httpStatus: normalized.httpStatus,
      code: normalized.code,
      userMessage: ADMIN_VECTOR_STORE_UPLOAD_DISPATCH_ERROR_MESSAGE,
    });
  }

  if (uploadedBeforeFailure > 0) {
    const base = normalized.userMessage;
    return new ApiError({
      httpStatus: normalized.httpStatus,
      code: normalized.code,
      userMessage:
        `«${filename}» kunne ikke uploades. ${uploadedBeforeFailure} af ${total} filer blev uploadet. `
        + `Vector store findes med delvise uploads — slet den i listen før du prøver igen.`
        + (base && !base.includes(filename) ? ` (${base})` : ''),
    });
  }

  if (normalized.code && normalized.code !== 'not_found') {
    return normalized;
  }

  return new ApiError({
    httpStatus: normalized.httpStatus,
    code: normalized.code,
    userMessage: `«${filename}» kunne ikke uploades.`,
  });
}

function createMockAdminVectorStore(input: CreateVectorStoreInput): VectorStore {
  const firstFile = input.files[0];
  const created: VectorStore = {
    id: uid(),
    navn: input.navn,
    beskrivelse: input.beskrivelse,
    tilladChunking: input.tilladChunking,
    synligForLaerere: input.synligForLaerere ?? false,
    uploadedFile: firstFile ? fileToUploadedMetadata(firstFile) : null,
    chunkSize: input.chunkSize,
    chunkOverlap: input.chunkOverlap,
    defaultTopK: input.defaultTopK,
    createdAt: nowISO(),
    updatedAt: nowISO(),
  };
  mockAdminVectorStores.unshift(created);
  seedMockDocumentsForStore(created.id, input.files);
  return { ...created };
}

export async function createVectorStoreRecord(
  input: Pick<
    CreateVectorStoreInput,
    'navn' | 'beskrivelse' | 'chunkSize' | 'chunkOverlap' | 'defaultTopK'
  > & {
    synligForLaerere?: boolean;
  },
): Promise<VectorStore> {
  const body: CreateAdminVectorStoreRequest = {
    name: input.navn.trim(),
    description: input.beskrivelse.trim() || null,
    chunk_size: input.chunkSize ?? undefined,
    chunk_overlap: input.chunkOverlap ?? undefined,
    default_top_k: input.defaultTopK ?? undefined,
    visible_to_teachers: input.synligForLaerere ?? false,
  };

  const wire = await fetchJson<AdminVectorStoreSummaryWire>('/chat/admin/vector-stores', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    admin: true,
  });
  return wireToVectorStore(wire);
}

export async function uploadVectorStoreDocument(
  storeId: string,
  file: File,
  allowChunking: boolean,
): Promise<AdminDocumentSummaryWire> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('allow_chunking', allowChunking ? 'true' : 'false');

  const wire = await fetchMultipart<AdminDocumentSummaryWire>(
    `/chat/admin/vector-stores/${encodeApiSegment(storeId)}/documents`,
    formData,
    { admin: true },
  );
  validateDocumentWireTimestamps(wire);
  return wire;
}

async function deleteVectorStoreRecord(storeId: string): Promise<void> {
  await fetchJson<void>(`/chat/admin/vector-stores/${encodeApiSegment(storeId)}`, {
    method: 'DELETE',
    admin: true,
  });
}

export async function createVectorStore(
  input: CreateVectorStoreInput,
  onProgress?: (current: number, total: number) => void,
): Promise<VectorStore> {
  if (!USE_LIVE) {
    return createMockAdminVectorStore(input);
  }

  if (input.files.length === 0) {
    throw new ApiError({
      httpStatus: 422,
      code: null,
      userMessage: 'Vælg mindst én fil at uploade.',
    });
  }

  let createdId: string | null = null;
  let uploadedCount = 0;
  const total = input.files.length;

  try {
    const store = await createVectorStoreRecord(input);
    createdId = store.id;

    for (let index = 0; index < input.files.length; index += 1) {
      const file = input.files[index];
      onProgress?.(index + 1, total);
      try {
        await uploadVectorStoreDocument(store.id, file, input.tilladChunking);
        uploadedCount += 1;
      } catch (error) {
        throw buildUploadFailureError(error, file.name, uploadedCount, total);
      }
    }

    return store;
  } catch (error) {
    if (createdId && uploadedCount === 0) {
      try {
        await deleteVectorStoreRecord(createdId);
      } catch {
        // Best-effort rollback only; preserve the original upload error.
      }
    }
    throw mapAdminError(error, ADMIN_VECTOR_STORE_CREATE_ERROR_MESSAGE);
  }
}

export async function getVectorStore(id: string): Promise<VectorStore> {
  if (!USE_LIVE) {
    const row = mockAdminVectorStores.find((store) => store.id === id);
    if (!row) {
      throw new ApiError({
        httpStatus: 404,
        code: 'vector_store_not_found',
        userMessage: DOMAIN_ERROR_MESSAGES_DA.vector_store_not_found,
      });
    }
    return { ...row };
  }

  try {
    const wire = await fetchJson<AdminVectorStoreSummaryWire>(`/chat/admin/vector-stores/${encodeApiSegment(id)}`, {
      ...resolveDashboardAuth(),
    });
    return wireToVectorStore(wire);
  } catch (error) {
    throw mapAdminError(error, ADMIN_VECTOR_STORE_GET_ERROR_MESSAGE);
  }
}

export async function updateVectorStore(
  id: string,
  input: UpdateVectorStoreInput,
): Promise<VectorStore> {
  const body: UpdateAdminVectorStoreRequest = {
    name: input.navn.trim(),
    description: input.beskrivelse.trim() || null,
    chunk_size: input.chunkSize ?? undefined,
    chunk_overlap: input.chunkOverlap ?? undefined,
    default_top_k: input.defaultTopK ?? undefined,
  };
  if (input.synligForLaerere !== undefined) {
    body.visible_to_teachers = input.synligForLaerere;
  }

  if (!USE_LIVE) {
    const index = mockAdminVectorStores.findIndex((store) => store.id === id);
    if (index === -1) {
      throw new ApiError({
        httpStatus: 404,
        code: 'vector_store_not_found',
        userMessage: DOMAIN_ERROR_MESSAGES_DA.vector_store_not_found,
      });
    }
    const updated: VectorStore = {
      ...mockAdminVectorStores[index],
      navn: input.navn.trim(),
      beskrivelse: input.beskrivelse.trim(),
      synligForLaerere: input.synligForLaerere ?? mockAdminVectorStores[index].synligForLaerere ?? false,
      chunkSize: input.chunkSize ?? mockAdminVectorStores[index].chunkSize,
      chunkOverlap: input.chunkOverlap ?? mockAdminVectorStores[index].chunkOverlap,
      defaultTopK: input.defaultTopK ?? mockAdminVectorStores[index].defaultTopK,
      updatedAt: nowISO(),
    };
    mockAdminVectorStores[index] = updated;
    return { ...updated };
  }

  try {
    const wire = await fetchJson<AdminVectorStoreSummaryWire>(`/chat/admin/vector-stores/${encodeApiSegment(id)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      admin: true,
    });
    return wireToVectorStore(wire);
  } catch (error) {
    throw mapAdminError(error, ADMIN_VECTOR_STORE_UPDATE_ERROR_MESSAGE);
  }
}

export async function deleteVectorStore(id: string): Promise<void> {
  if (!USE_LIVE) {
    const index = mockAdminVectorStores.findIndex((store) => store.id === id);
    if (index === -1) {
      throw new ApiError({
        httpStatus: 404,
        code: 'vector_store_not_found',
        userMessage: DOMAIN_ERROR_MESSAGES_DA.vector_store_not_found,
      });
    }
    if (mockStoreLinkedByChatbot(id)) {
      throw mockVectorStoreInUseError();
    }
    mockAdminVectorStores.splice(index, 1);
    mockAdminDocuments.delete(id);
    return;
  }

  try {
    await fetchJson<void>(`/chat/admin/vector-stores/${encodeApiSegment(id)}`, {
      method: 'DELETE',
      admin: true,
    });
  } catch (error) {
    throw mapAdminError(error, ADMIN_VECTOR_STORE_DELETE_ERROR_MESSAGE);
  }
}

export async function listVectorStoreDocuments(storeId: string): Promise<VectorStoreDocument[]> {
  if (!USE_LIVE) {
    return [...(mockAdminDocuments.get(storeId) ?? [])];
  }

  try {
    const data = await fetchJson<AdminDocumentListResponse>(
      `/chat/admin/vector-stores/${encodeApiSegment(storeId)}/documents`,
      { admin: true },
    );
    return data.documents.map(wireToVectorStoreDocument);
  } catch (error) {
    throw mapAdminError(error, ADMIN_VECTOR_STORE_GET_ERROR_MESSAGE);
  }
}

export async function deleteVectorStoreDocument(documentId: string): Promise<void> {
  if (!USE_LIVE) {
    for (const [storeId, docs] of mockAdminDocuments.entries()) {
      const index = docs.findIndex((doc) => doc.id === documentId);
      if (index !== -1) {
        docs.splice(index, 1);
        mockAdminDocuments.set(storeId, docs);
        return;
      }
    }
    return;
  }

  try {
    await fetchJson<void>(`/chat/admin/documents/${encodeApiSegment(documentId)}`, {
      method: 'DELETE',
      admin: true,
    });
  } catch (error) {
    if (error instanceof ApiError && error.httpStatus === 404 && error.code === 'not_found') {
      return;
    }
    throw error;
  }
}

export async function saveVectorStoreEdits(
  storeId: string,
  input: SaveVectorStoreEditsInput,
  onProgress?: (current: number, total: number) => void,
): Promise<VectorStore> {
  await updateVectorStore(storeId, {
    navn: input.navn,
    beskrivelse: input.beskrivelse,
    synligForLaerere: input.synligForLaerere,
  });

  for (const documentId of input.removedDocumentIds) {
    await deleteVectorStoreDocument(documentId);
  }

  const total = input.newFiles.length;
  for (let index = 0; index < input.newFiles.length; index += 1) {
    const file = input.newFiles[index];
    onProgress?.(index + 1, total);
    try {
      if (!USE_LIVE) {
        const docs = mockAdminDocuments.get(storeId) ?? [];
        const now = nowISO();
        docs.unshift({
          id: uid(),
          vectorStoreId: storeId,
          filename: file.name,
          status: 'processing',
          errorMessage: null,
          createdAt: now,
          updatedAt: now,
        });
        mockAdminDocuments.set(storeId, docs);
      } else {
        await uploadVectorStoreDocument(storeId, file, input.tilladChunking);
      }
    } catch (error) {
      throw buildUploadFailureError(error, file.name, index, total);
    }
  }

  return getVectorStore(storeId);
}

function slugifyStoreName(storeName: string): string {
  const slug = storeName
    .replace(/[^A-Za-z0-9_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug || 'vector-store';
}

export async function downloadVectorStoreMarkdown(
  storeId: string,
  storeName: string,
): Promise<void> {
  if (!USE_LIVE) {
    throw new ApiError({
      httpStatus: 400,
      code: null,
      userMessage: 'Download virker kun mod live API.',
    });
  }
  const blob = await fetchBlob(
    `/chat/admin/vector-stores/${encodeApiSegment(storeId)}/export`,
    { admin: true },
  );
  const date = new Date();
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const filename = `${slugifyStoreName(storeName)}-${yyyy}-${mm}-${dd}.md`;
  const url = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.click();
  } finally {
    URL.revokeObjectURL(url);
  }
}
